import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Plus, ChevronRight, ChevronDown, Trash2, ArrowUp, ArrowDown,
  GitBranch, Save, MapPin, StickyNote, Users, Loader2, CheckCircle2, AlertCircle,
  FileText, Paperclip, Star, Search,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from './ui/alert-dialog';
import { api } from '../App';
import { toast } from 'sonner';

// ---------- id + node factories ----------
const genId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : 'n_' + Math.random().toString(36).slice(2) + Date.now().toString(36);

const newSurvey = () => ({ sy_no: '', extent: '', unit: '' });
const newNode = (owner_name = '') => ({
  id: genId(), owner_name, surveys: [newSurvey()], remarks: '', documents: [], children: [],
});

// ---------- pure tree helpers (immutable) ----------
const updateNode = (nodes, id, patch) =>
  nodes.map((n) =>
    n.id === id ? { ...n, ...patch } : { ...n, children: updateNode(n.children, id, patch) });

const addChildren = (nodes, parentId, kids) =>
  nodes.map((n) =>
    n.id === parentId
      ? { ...n, children: [...n.children, ...kids] }
      : { ...n, children: addChildren(n.children, parentId, kids) });

const removeSubtree = (nodes, id) =>
  nodes.filter((n) => n.id !== id).map((n) => ({ ...n, children: removeSubtree(n.children, id) }));

// remove a node but lift its children into its slot (same order/position)
const removeAndPromote = (nodes, id) => {
  const out = [];
  for (const n of nodes) {
    if (n.id === id) out.push(...n.children.map((c) => ({ ...c })));
    else out.push({ ...n, children: removeAndPromote(n.children, id) });
  }
  return out;
};

const moveNode = (nodes, id, dir) => {
  const idx = nodes.findIndex((n) => n.id === id);
  if (idx !== -1) {
    const target = dir === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= nodes.length) return nodes;
    const copy = [...nodes];
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    return copy;
  }
  return nodes.map((n) => ({ ...n, children: moveNode(n.children, id, dir) }));
};

const countDescendants = (node) =>
  node.children.reduce((acc, c) => acc + 1 + countDescendants(c), 0);

const collectAllIds = (nodes, acc = []) => {
  nodes.forEach((n) => { acc.push(n.id); collectAllIds(n.children, acc); });
  return acc;
};

// ids of nodes whose owner name is blank (used for the Save guard)
const collectInvalidIds = (nodes, acc = []) => {
  nodes.forEach((n) => {
    if (!(n.owner_name || '').trim()) acc.push(n.id);
    collectInvalidIds(n.children, acc);
  });
  return acc;
};

// normalize server data -> ensure shape
const normalizeTree = (nodes) =>
  (nodes || []).map((n) => ({
    id: n.id || genId(),
    owner_name: n.owner_name || '',
    surveys: (n.surveys && n.surveys.length ? n.surveys : [newSurvey()]).map((s) => ({
      sy_no: s.sy_no || '', extent: s.extent || '', unit: s.unit || '',
    })),
    remarks: n.remarks || '',
    documents: (n.documents || []).map((d) => ({
      entry_id: d.entry_id,
      role: d.role === 'main' ? 'main' : 'supporting',
      remarks: d.remarks || '',
    })),
    children: normalizeTree(n.children),
  }));

// find a node anywhere in the tree by id
const findNodeById = (nodes, id) => {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = findNodeById(n.children, id);
    if (found) return found;
  }
  return null;
};

const UNIT_SUGGESTIONS = ['Acres', 'Guntas', 'Sq. Ft.', 'Sq. Mtrs.', 'Cents', 'Anas'];

// =====================================================================
const NodeCard = ({
  node, depth, index, siblingCount, collapsed, onToggle,
  onPatch, onAddChild, onAddBranches, onMove, onDelete, invalidIds, showErrors,
  docOptions, onAttach,
}) => {
  const isCollapsed = collapsed.has(node.id);
  const childCount = node.children.length;
  const descCount = countDescendants(node);
  const hasError = showErrors && invalidIds.has(node.id);
  const docCount = (node.documents || []).length;

  const setField = (patch) => onPatch(node.id, patch);
  const setDocRole = (di, role) =>
    setField({ documents: node.documents.map((d, i) => (i === di ? { ...d, role } : d)) });
  const setDocRemark = (di, remarks) =>
    setField({ documents: node.documents.map((d, i) => (i === di ? { ...d, remarks } : d)) });
  const removeDoc = (di) =>
    setField({ documents: node.documents.filter((_, i) => i !== di) });
  const setSurvey = (si, patch) => {
    const surveys = node.surveys.map((s, i) => (i === si ? { ...s, ...patch } : s));
    setField({ surveys });
  };
  const addSurveyRow = () => setField({ surveys: [...node.surveys, newSurvey()] });
  const removeSurveyRow = (si) => {
    const surveys = node.surveys.filter((_, i) => i !== si);
    setField({ surveys: surveys.length ? surveys : [newSurvey()] });
  };

  const sySummary = node.surveys
    .filter((s) => s.sy_no || s.extent)
    .map((s) => [s.sy_no, [s.extent, s.unit].filter(Boolean).join(' ')].filter(Boolean).join(' – '))
    .join(', ');

  const borderColors = ['#1e3a5f', '#2563eb', '#7c3aed', '#0d9488', '#ea580c', '#059669'];
  const accent = borderColors[depth % borderColors.length];

  return (
    <div
      className="relative"
      style={{ marginLeft: depth === 0 ? 0 : 32 }}
      data-testid={`flow-node-${node.id}`}
    >
      {depth > 0 && (
        <>
          <div className="absolute -left-[18px] top-0 bottom-2 w-[2px] bg-slate-200" aria-hidden />
          <div className="absolute -left-[18px] top-[26px] w-[16px] h-[2px] bg-slate-200" aria-hidden />
        </>
      )}
      <div
        className={`rounded-lg border bg-white shadow-sm mb-2 ${hasError ? 'ring-1 ring-red-400 border-red-300' : ''}`}
        style={{ borderLeft: `4px solid ${hasError ? '#dc2626' : accent}` }}
      >
        {/* Header row */}
        <div className="flex items-center gap-2 px-3 py-2">
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            className="text-slate-500 hover:text-slate-800 shrink-0"
            data-testid={`flow-toggle-${node.id}`}
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
          </button>

          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Users size={15} className="shrink-0" style={{ color: hasError ? '#dc2626' : accent }} />
            {isCollapsed ? (
              <div className="min-w-0">
                <span className="font-semibold text-slate-800 truncate block text-sm">
                  {node.owner_name || <span className="italic text-red-500">Unnamed owner (name required)</span>}
                </span>
                {sySummary && (
                  <span className="text-xs text-slate-500 truncate block">{sySummary}</span>
                )}
              </div>
            ) : (
              <div className="flex-1">
                <Input
                  value={node.owner_name}
                  onChange={(e) => setField({ owner_name: e.target.value })}
                  placeholder="Owner name (required)"
                  className={`h-8 font-semibold text-slate-800 ${hasError ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                  data-testid={`flow-owner-input-${node.id}`}
                />
                {hasError && (
                  <span className="text-[11px] text-red-600 mt-0.5 block" data-testid={`flow-owner-error-${node.id}`}>
                    Owner name is required before saving
                  </span>
                )}
              </div>
            )}
          </div>

          {docCount > 0 && (
            <span
              className="flex items-center gap-1 text-[11px] font-medium text-[#1e3a5f] bg-blue-50 rounded-full px-2 py-0.5 shrink-0"
              data-testid={`flow-doc-count-${node.id}`}
              title={`${docCount} document(s) attached`}
            >
              <Paperclip size={11} /> {docCount}
            </span>
          )}

          {childCount > 0 && (
            <span
              className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 shrink-0"
              data-testid={`flow-child-count-${node.id}`}
            >
              {childCount} {childCount === 1 ? 'branch' : 'branches'}
              {descCount !== childCount ? ` · ${descCount} total` : ''}
            </span>
          )}

          {/* Move controls */}
          <div className="flex items-center shrink-0">
            <Button
              variant="ghost" size="icon" className="h-7 w-7"
              disabled={index === 0}
              onClick={() => onMove(node.id, 'up')}
              data-testid={`flow-move-up-${node.id}`}
              title="Move up"
            >
              <ArrowUp size={15} />
            </Button>
            <Button
              variant="ghost" size="icon" className="h-7 w-7"
              disabled={index === siblingCount - 1}
              onClick={() => onMove(node.id, 'down')}
              data-testid={`flow-move-down-${node.id}`}
              title="Move down"
            >
              <ArrowDown size={15} />
            </Button>
            <Button
              variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => onDelete(node)}
              data-testid={`flow-delete-${node.id}`}
              title="Delete node"
            >
              <Trash2 size={15} />
            </Button>
          </div>
        </div>

        {/* Body (only when expanded) */}
        {!isCollapsed && (
          <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-3">
            {/* Surveys */}
            <div>
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-1.5">
                <MapPin size={13} /> Survey Numbers
              </div>
              <div className="space-y-1.5">
                {node.surveys.map((s, si) => (
                  <div key={si} className="flex items-center gap-1.5">
                    <Input
                      value={s.sy_no}
                      onChange={(e) => setSurvey(si, { sy_no: e.target.value })}
                      placeholder="Sy. No."
                      className="h-8 flex-1"
                      data-testid={`flow-syno-${node.id}-${si}`}
                    />
                    <Input
                      value={s.extent}
                      onChange={(e) => setSurvey(si, { extent: e.target.value })}
                      placeholder="Extent"
                      className="h-8 w-24"
                      data-testid={`flow-extent-${node.id}-${si}`}
                    />
                    <Input
                      value={s.unit}
                      onChange={(e) => setSurvey(si, { unit: e.target.value })}
                      placeholder="Unit"
                      list="flow-unit-suggestions"
                      className="h-8 w-24"
                      data-testid={`flow-unit-${node.id}-${si}`}
                    />
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600 shrink-0"
                      onClick={() => removeSurveyRow(si)}
                      data-testid={`flow-remove-survey-${node.id}-${si}`}
                      title="Remove survey row"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                variant="ghost" size="sm" className="h-7 mt-1 text-xs text-[#1e3a5f]"
                onClick={addSurveyRow}
                data-testid={`flow-add-survey-${node.id}`}
              >
                <Plus size={13} className="mr-1" /> Add Sy. No. row
              </Button>
            </div>

            {/* Remarks */}
            <div>
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-1.5">
                <StickyNote size={13} /> Node Remarks
              </div>
              <Textarea
                value={node.remarks}
                onChange={(e) => setField({ remarks: e.target.value })}
                placeholder="Remarks specific to this owner / node…"
                className="min-h-[56px] text-sm"
                data-testid={`flow-remarks-${node.id}`}
              />
            </div>

            {/* Attached documents */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                  <FileText size={13} /> Attached Documents
                </div>
                <Button
                  variant="outline" size="sm" className="h-7 text-xs"
                  onClick={() => onAttach(node.id)}
                  data-testid={`flow-attach-doc-${node.id}`}
                >
                  <Paperclip size={13} className="mr-1" /> Attach Document
                </Button>
              </div>
              {docCount === 0 ? (
                <p className="text-xs text-slate-400 italic">No documents attached to this node.</p>
              ) : (
                <div className="space-y-1.5" data-testid={`flow-doc-list-${node.id}`}>
                  {node.documents.map((d, di) => {
                    const opt = docOptions?.[d.entry_id];
                    const isMain = d.role === 'main';
                    return (
                      <div
                        key={di}
                        className="rounded-md border bg-slate-50/60 p-2"
                        data-testid={`flow-doc-item-${node.id}-${di}`}
                      >
                        <div className="flex items-start gap-2">
                          <button
                            type="button"
                            onClick={() => setDocRole(di, isMain ? 'supporting' : 'main')}
                            title={isMain ? 'Marked as Main — click to make Supporting' : 'Mark as Main document'}
                            className={`shrink-0 flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                              isMain ? 'bg-amber-100 text-amber-800' : 'bg-slate-200 text-slate-600'
                            }`}
                            data-testid={`flow-doc-role-${node.id}-${di}`}
                          >
                            {isMain ? <Star size={11} className="fill-amber-500 text-amber-500" /> : <Paperclip size={11} />}
                            {isMain ? 'Main' : 'Supporting'}
                          </button>
                          <div className="min-w-0 flex-1">
                            {opt ? (
                              <div className="text-sm text-slate-800">
                                <span className="font-semibold">#{opt.serial_number}</span>{' '}
                                {opt.document_type}
                              </div>
                            ) : (
                              <div className="text-sm text-red-500 italic">
                                Document no longer exists in this case
                              </div>
                            )}
                            {opt?.descriptor && (
                              <div className="text-xs text-slate-500 truncate">{opt.descriptor}</div>
                            )}
                            <Input
                              value={d.remarks}
                              onChange={(e) => setDocRemark(di, e.target.value)}
                              placeholder="Document remark (optional)"
                              className="h-7 text-xs mt-1"
                              data-testid={`flow-doc-remark-${node.id}-${di}`}
                            />
                          </div>
                          <Button
                            variant="ghost" size="icon"
                            className="h-7 w-7 text-slate-400 hover:text-red-600 shrink-0"
                            onClick={() => removeDoc(di)}
                            data-testid={`flow-doc-remove-${node.id}-${di}`}
                            title="Remove attachment (does not delete the document)"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Node actions */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                variant="outline" size="sm" className="h-7 text-xs"
                onClick={() => onAddChild(node.id)}
                data-testid={`flow-add-child-${node.id}`}
              >
                <Plus size={13} className="mr-1" /> Add Child
              </Button>
              <Button
                variant="outline" size="sm" className="h-7 text-xs"
                onClick={() => onAddBranches(node.id)}
                data-testid={`flow-add-branches-${node.id}`}
              >
                <GitBranch size={13} className="mr-1" /> Add Multiple Branches
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Children (recursive call via createElement to avoid self-referential JSX) */}
      {!isCollapsed && childCount > 0 && (
        <div>
          {node.children.map((c, ci) =>
            React.createElement(NodeCard, {
              key: c.id,
              node: c,
              depth: depth + 1,
              index: ci,
              siblingCount: childCount,
              collapsed,
              onToggle,
              onPatch,
              onAddChild,
              onAddBranches,
              onMove,
              onDelete,
              invalidIds,
              showErrors,
              docOptions,
              onAttach,
            })
          )}
        </div>
      )}
    </div>
  );
};

const ManualFlowEditor = ({ caseId }) => {
  const [tree, setTree] = useState([]);
  const [collapsed, setCollapsed] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedJson, setSavedJson] = useState('[]');
  const [lastSaved, setLastSaved] = useState(null);
  const [lastSavedBy, setLastSavedBy] = useState(null);

  const [branchDlg, setBranchDlg] = useState({ open: false, parentId: null, count: 2 });
  const [delDlg, setDelDlg] = useState({ open: false, node: null });
  const [showErrors, setShowErrors] = useState(false);
  const [docList, setDocList] = useState([]); // [{entry_id, serial_number, document_type, descriptor}]
  const [attachDlg, setAttachDlg] = useState({ open: false, nodeId: null });
  const [attachSearch, setAttachSearch] = useState('');

  const dirty = useMemo(() => JSON.stringify(tree) !== savedJson, [tree, savedJson]);
  const invalidIds = useMemo(() => new Set(collectInvalidIds(tree)), [tree]);
  const docOptions = useMemo(() => {
    const m = {};
    docList.forEach((d) => { m[d.entry_id] = d; });
    return m;
  }, [docList]);

  useEffect(() => { if (invalidIds.size === 0) setShowErrors(false); }, [invalidIds]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [flowRes, docsRes] = await Promise.all([
        api.get(`/cases/${caseId}/manual-ownership-flow`),
        api.get(`/cases/${caseId}/flow-document-options`),
      ]);
      const t = normalizeTree(flowRes.data.tree);
      setTree(t);
      setSavedJson(JSON.stringify(t));
      setLastSaved(flowRes.data.updated_at || null);
      setLastSavedBy(flowRes.data.updated_by_name || null);
      setDocList(Array.isArray(docsRes.data) ? docsRes.data : []);
    } catch (e) {
      toast.error('Failed to load ownership flow');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  // warn on browser unload with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const onToggle = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(collectAllIds(tree)));

  const onPatch = (id, patch) => setTree((t) => updateNode(t, id, patch));
  const onAddRoot = () => {
    const n = newNode();
    setTree((t) => [...t, n]);
  };
  const onAddChild = (parentId) => {
    setTree((t) => addChildren(t, parentId, [newNode()]));
    setCollapsed((prev) => { const nx = new Set(prev); nx.delete(parentId); return nx; });
  };
  const openBranches = (parentId) => setBranchDlg({ open: true, parentId, count: 2 });
  const confirmBranches = () => {
    const n = Math.max(1, Math.min(50, parseInt(branchDlg.count, 10) || 1));
    const kids = Array.from({ length: n }, () => newNode());
    setTree((t) => addChildren(t, branchDlg.parentId, kids));
    setCollapsed((prev) => { const nx = new Set(prev); nx.delete(branchDlg.parentId); return nx; });
    setBranchDlg({ open: false, parentId: null, count: 2 });
  };
  const onMove = (id, dir) => setTree((t) => moveNode(t, id, dir));

  const openDelete = (node) => setDelDlg({ open: true, node });
  const doDelete = (mode) => {
    const id = delDlg.node.id;
    setTree((t) => (mode === 'promote' ? removeAndPromote(t, id) : removeSubtree(t, id)));
    setDelDlg({ open: false, node: null });
  };

  const openAttach = (nodeId) => { setAttachSearch(''); setAttachDlg({ open: true, nodeId }); };
  const attachDoc = (entryId) => {
    const nodeId = attachDlg.nodeId;
    const node = findNodeById(tree, nodeId);
    if (node && node.documents.some((d) => d.entry_id === entryId)) {
      toast.info('Already attached to this node');
      return;
    }
    setTree((t) => updateNode(t, nodeId, {
      documents: [...(node?.documents || []), { entry_id: entryId, role: 'supporting', remarks: '' }],
    }));
  };

  const save = async () => {
    if (invalidIds.size > 0) {
      setShowErrors(true);
      setCollapsed(new Set()); // expand all so every flagged node is visible
      toast.error(
        `${invalidIds.size} owner ${invalidIds.size === 1 ? 'node needs a name' : 'nodes need a name'} before saving`
      );
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/cases/${caseId}/manual-ownership-flow`, { tree });
      setSavedJson(JSON.stringify(tree));
      setLastSaved(data.updated_at || new Date().toISOString());
      setLastSavedBy(data.updated_by_name || null);
      toast.success('Ownership flow saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString(undefined, {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500" data-testid="flow-loading">
        <Loader2 className="animate-spin mr-2" size={20} /> Loading…
      </div>
    );
  }

  return (
    <div data-testid="manual-flow-editor">
      <datalist id="flow-unit-suggestions">
        {UNIT_SUGGESTIONS.map((u) => <option key={u} value={u} />)}
      </datalist>

      {/* Toolbar */}
      <div className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur border rounded-lg px-3 py-2 mb-4 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onAddRoot} className="bg-[#1e3a5f] hover:bg-[#152a45]" data-testid="flow-add-root-btn">
          <Plus size={15} className="mr-1" /> Add Root Owner
        </Button>
        <Button size="sm" variant="outline" onClick={expandAll} data-testid="flow-expand-all-btn">Expand all</Button>
        <Button size="sm" variant="outline" onClick={collapseAll} data-testid="flow-collapse-all-btn">Collapse all</Button>

        <div className="flex-1" />

        <div className="flex items-center gap-2 text-xs" data-testid="flow-dirty-indicator">
          {showErrors && invalidIds.size > 0 ? (
            <span className="flex items-center gap-1 text-red-600 font-medium" data-testid="flow-validation-error">
              <AlertCircle size={14} /> {invalidIds.size} owner {invalidIds.size === 1 ? 'name is' : 'names are'} required
            </span>
          ) : dirty ? (
            <span className="flex items-center gap-1 text-amber-600 font-medium">
              <AlertCircle size={14} /> Unsaved changes
            </span>
          ) : lastSaved ? (
            <span className="flex items-center gap-1 text-green-700">
              <CheckCircle2 size={14} /> Saved · {fmtTime(lastSaved)}
              {lastSavedBy ? ` by ${lastSavedBy}` : ''}
            </span>
          ) : (
            <span className="text-slate-400">Not saved yet</span>
          )}
        </div>

        <Button
          size="sm"
          onClick={save}
          disabled={saving || !dirty}
          className="bg-emerald-700 hover:bg-emerald-800"
          data-testid="flow-save-btn"
        >
          {saving ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Save size={15} className="mr-1" />}
          Save
        </Button>
      </div>

      {/* Tree */}
      {tree.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed rounded-lg bg-white" data-testid="flow-empty-state">
          <Users size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">No owners yet</p>
          <p className="text-slate-400 text-sm mt-1 mb-4">Start building the title flow by adding the first (root) owner.</p>
          <Button onClick={onAddRoot} className="bg-[#1e3a5f] hover:bg-[#152a45]" data-testid="flow-add-root-empty-btn">
            <Plus size={16} className="mr-1" /> Add Root Owner
          </Button>
        </div>
      ) : (
        <div className="space-y-1">
          {tree.map((n, i) => (
            <NodeCard
              key={n.id}
              node={n}
              depth={0}
              index={i}
              siblingCount={tree.length}
              collapsed={collapsed}
              onToggle={onToggle}
              onPatch={onPatch}
              onAddChild={onAddChild}
              onAddBranches={openBranches}
              onMove={onMove}
              onDelete={openDelete}
              invalidIds={invalidIds}
              showErrors={showErrors}
              docOptions={docOptions}
              onAttach={openAttach}
            />
          ))}
        </div>
      )}

      {/* Add Multiple Branches dialog */}
      <Dialog open={branchDlg.open} onOpenChange={(o) => !o && setBranchDlg({ open: false, parentId: null, count: 2 })}>
        <DialogContent data-testid="flow-branches-dialog">
          <DialogHeader>
            <DialogTitle>Add Multiple Branches</DialogTitle>
            <DialogDescription>
              Create several child owner nodes at once under this node (e.g. a partition into several shares).
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium text-slate-600">Number of branches</label>
            <Input
              type="number" min={1} max={50}
              value={branchDlg.count}
              onChange={(e) => setBranchDlg((d) => ({ ...d, count: e.target.value }))}
              className="mt-1 w-32"
              data-testid="flow-branches-count-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBranchDlg({ open: false, parentId: null, count: 2 })} data-testid="flow-branches-cancel">
              Cancel
            </Button>
            <Button onClick={confirmBranches} className="bg-[#1e3a5f] hover:bg-[#152a45]" data-testid="flow-branches-confirm">
              Add Branches
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attach Document dialog */}
      <Dialog open={attachDlg.open} onOpenChange={(o) => !o && setAttachDlg({ open: false, nodeId: null })}>
        <DialogContent className="max-w-2xl" data-testid="flow-attach-dialog">
          <DialogHeader>
            <DialogTitle>Attach a DDR Document</DialogTitle>
            <DialogDescription>
              Pick from this case's Documents Furnished. Serial numbers are live and update automatically.
              Attaching here never changes the underlying DDR document.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              value={attachSearch}
              onChange={(e) => setAttachSearch(e.target.value)}
              placeholder="Search by serial no., document type or descriptor…"
              className="pl-8 h-9"
              data-testid="flow-attach-search"
            />
          </div>
          <div className="max-h-[50vh] overflow-y-auto space-y-1.5 pr-1 mt-2">
            {(() => {
              const node = findNodeById(tree, attachDlg.nodeId);
              const attachedSet = new Set((node?.documents || []).map((d) => d.entry_id));
              const q = attachSearch.trim().toLowerCase();
              const filtered = docList.filter((d) =>
                !q ||
                String(d.serial_number).includes(q) ||
                (d.document_type || '').toLowerCase().includes(q) ||
                (d.descriptor || '').toLowerCase().includes(q)
              );
              if (docList.length === 0) {
                return <p className="text-sm text-slate-400 italic py-6 text-center">This case has no DDR documents yet.</p>;
              }
              if (filtered.length === 0) {
                return <p className="text-sm text-slate-400 italic py-6 text-center">No documents match your search.</p>;
              }
              return filtered.map((d) => {
                const attached = attachedSet.has(d.entry_id);
                return (
                  <div
                    key={d.entry_id}
                    className="flex items-center gap-3 rounded-md border p-2"
                    data-testid={`flow-attach-option-${d.entry_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-800">
                        <span className="font-semibold">#{d.serial_number}</span> {d.document_type}
                      </div>
                      {d.descriptor && <div className="text-xs text-slate-500 truncate">{d.descriptor}</div>}
                    </div>
                    <Button
                      size="sm"
                      variant={attached ? 'outline' : 'default'}
                      disabled={attached}
                      onClick={() => attachDoc(d.entry_id)}
                      className={attached ? '' : 'bg-[#1e3a5f] hover:bg-[#152a45]'}
                      data-testid={`flow-attach-add-${d.entry_id}`}
                    >
                      {attached ? 'Attached' : (<><Paperclip size={13} className="mr-1" /> Attach</>)}
                    </Button>
                  </div>
                );
              });
            })()}
          </div>
          <DialogFooter>
            <Button onClick={() => setAttachDlg({ open: false, nodeId: null })} data-testid="flow-attach-done" className="bg-[#1e3a5f] hover:bg-[#152a45]">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={delDlg.open} onOpenChange={(o) => !o && setDelDlg({ open: false, node: null })}>
        <AlertDialogContent data-testid="flow-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this node?</AlertDialogTitle>
            <AlertDialogDescription>
              {delDlg.node && countDescendants(delDlg.node) > 0 ? (
                <>
                  <span className="font-medium text-slate-700">
                    “{delDlg.node.owner_name || 'Unnamed owner'}”
                  </span>{' '}
                  has {countDescendants(delDlg.node)} descendant node(s). Choose how to handle its child branches.
                </>
              ) : (
                <>This will permanently remove <span className="font-medium text-slate-700">“{delDlg.node?.owner_name || 'Unnamed owner'}”</span> from the tree.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            {delDlg.node && countDescendants(delDlg.node) > 0 && (
              <Button
                variant="outline"
                onClick={() => doDelete('promote')}
                data-testid="flow-delete-promote"
              >
                Delete only this node — keep &amp; promote its children
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => doDelete('subtree')}
              data-testid="flow-delete-subtree"
            >
              {delDlg.node && countDescendants(delDlg.node) > 0
                ? 'Delete this node and ALL its children'
                : 'Delete node'}
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="flow-delete-cancel">Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ManualFlowEditor;
